import { Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { StoreModuleTypes } from '@/constants/store-constants';
import store from '@/store';
import { TaskDateFilter, DEFAULT_TASK_DATE_FILTERS } from '@/families/utils/task-filter-utils';

const STORAGE_KEY = 'crm_family_hub_todo_filters';

interface StorageItem {
    value: TaskDateFilter[];
    timestamp: number;
}

/**
 * Validates that the stored filters are valid TaskDateFilter values
 */
function validateFilters(filters: any): filters is TaskDateFilter[] {
    if (!Array.isArray(filters)) {
        return false;
    }

    const validFilters: TaskDateFilter[] = ['past', 'today', 'future'];
    return filters.every(filter => validFilters.includes(filter)) && filters.length > 0;
}

/**
 * Saves the To Do tab button states to sessionStorage
 */
function saveToStorage(value: TaskDateFilter[]) {
    try {
        const storageItem: StorageItem = {
            value: value,
            timestamp: Date.now()
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(storageItem));
    } catch {
        // Fail silently if storage is not available (privacy mode, quota exceeded, etc.)
    }
}

/**
 * Loads the To Do tab button states from sessionStorage
 * Returns null if storage is unavailable, corrupted, or contains invalid data
 */
function loadFromStorage(): TaskDateFilter[] | null {
    try {
        const item = sessionStorage.getItem(STORAGE_KEY);
        if (!item) {
            return null;
        }

        const storageItem: StorageItem = JSON.parse(item);

        // Validate the stored data
        if (!validateFilters(storageItem.value)) {
            // Clear corrupted data
            sessionStorage.removeItem(STORAGE_KEY);
            return null;
        }

        return storageItem.value;
    } catch {
        // If parsing fails or storage is unavailable, return null
        // This ensures the UI falls back to default states
        try {
            sessionStorage.removeItem(STORAGE_KEY);
        } catch {
            // Ignore errors when trying to clean up
        }
        return null;
    }
}

export interface FamilyHubFiltersState {
    toDoDateFilters: TaskDateFilter[];
}

@Module({
    namespaced: true,
    name: StoreModuleTypes.FAMILY_HUB_FILTERS,
    dynamic: true,
    store: store
})
export class FamilyHubFiltersStore extends VuexModule implements FamilyHubFiltersState {
    toDoDateFilters: TaskDateFilter[] = loadFromStorage() ?? [...DEFAULT_TASK_DATE_FILTERS];

    @Mutation
    setToDoDateFilters(filters: TaskDateFilter[]) {
        // Validate filters before saving
        if (!validateFilters(filters)) {
            // If invalid, fall back to defaults
            this.toDoDateFilters = [...DEFAULT_TASK_DATE_FILTERS];
            saveToStorage(this.toDoDateFilters);
            return;
        }

        this.toDoDateFilters = filters;
        saveToStorage(filters);
    }

    @Mutation
    clearFilters() {
        try {
            sessionStorage.removeItem(STORAGE_KEY);
        } catch {
            // Fail silently if storage is not available
        }
        this.toDoDateFilters = [...DEFAULT_TASK_DATE_FILTERS];
    }

    get storedToDoDateFilters(): TaskDateFilter[] {
        return this.toDoDateFilters;
    }
}
